import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Logger } from '@core/logger';
import { TagsDate } from '@core/util/datetime';
import { MatterDocumentFlows } from '@models/documents';
import {
	Action,
	NgxsOnInit,
	Select,
	Selector,
	State,
	StateContext,
	Store,
} from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { cloneDeep } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { debounceTime, retry, switchMap, take } from 'rxjs/operators';
import { IAdjustmentTags } from './models/adjustments.model';
import { AppointmentTags } from './models/appointment.model';
import { BaseTag } from './models/base.model';
import { BorrowerTags } from './models/borrower.model';
import { BusinessTags } from './models/business.model';
import { UsersOnFileTags } from './models/businessUser.model';
import { ClientTags } from './models/clients.model';
import { CommissionTags } from './models/commission.model';
import { ComplianceRequestTags } from './models/complianceRequests.model';
import { DebtsTags } from './models/debts.model';
import { EncumbranceTags } from './models/encumbrance.model';
import { JurisdictionsTags } from './models/jurisdictions.model';
import { LTTDepositTags } from './models/lttAndDeposit.model';
import { MortgagesTags } from './models/mortgages.model';
import { NoteTags } from './models/notes.model';
import { OtherPartyTags } from './models/otherParty.model';
import { ClientPlurals } from './models/plurals.model';
import { PropertyTags } from './models/property.model';
import { PurchaserAndVendorTags } from './models/purchasersAndVendors.model';
import { RequisitionTags } from './models/requisition.model';
import { SalutationTags } from './models/salutations.model';
import { SignatoryTags } from './models/signatories.model';
import { SignatureTags } from './models/signatures.model';
import { StatementOfAccountTags } from './models/statementOfAccount.model';
import { TitleInsuranceTag } from './models/titleInsurance.model';
import { AmountInTrustTags, TrustLedgerTags } from './models/trustLedger.model';
import { UndertakingTags } from './models/undertakings.model';
import { AdjustmentTagsSelector } from './selectors/adjustments.selector';
import { AppointmentTagSelector } from './selectors/appointment.selector';
import { BaseTagSelector } from './selectors/base.selector';
import { BorrowerTagsSelector } from './selectors/borrower.selector';
import { BusinessTagSelector } from './selectors/business.selector';
import { BusinessUserTagSelector } from './selectors/businessUser.selector';
import { ClientTagsSelectors } from './selectors/clients.selector';
import { CommissionCalculatedTags } from './selectors/commission.selector';
import { ComplianceRequestsTagsSelector } from './selectors/complianceRequests.selector';
import { DebtsTagsSelector } from './selectors/debts.selector';
import { EncumbranceTagsSelector } from './selectors/encumbrance.selector';
import { JurisdictionsTagsSelector } from './selectors/jurisdictions.selector';
import { LTTTagsSelector } from './selectors/lttAndDeposit.selector';
import { MortgagesTagSelector } from './selectors/mortgages.selector';
import { NotesTagsSelectors } from './selectors/notes.selector';
import { OtherPartyTagsSelector } from './selectors/otherParty.selector';
import { PluralTagsSelector } from './selectors/plurals.selector';
import { PropertyTagsSelector } from './selectors/property.selector';
import { PurchasersAndVendorTagsSelector } from './selectors/purchasersAndVendors.selector';
import { RequisitionTagsSelector } from './selectors/requisition.selector';
import { SalutationsTagSelector } from './selectors/salutations.selector';
import { SignatoriesTagSelectors } from './selectors/signatories.selector';
import { SignatureTagsSelectors } from './selectors/signatures.selector';
import { StatementOfAccountTagsSelector } from './selectors/statementOfAccount.selector';
import { TitleInsuranceTagsSelectors } from './selectors/titleInsurance.selector';
import { TrustLedgerTagsSelector } from './selectors/trustLedger.selector';
import { UndertakingsTagSelector } from './selectors/undertakings.selector';
import { TagRepoService } from './tags.firestore';
import { CustomTags, DefaultCustomTags, TagModel } from './tags.models';
import { ClientIntakeSelector } from './selectors/clientIntake.selector';
import { ClientIntakeFormTags } from './models/clientIntake.model';
import { ForceAddTagsToFirestore } from './tags.actions';

@State<TagModel>({
	defaults: {
		tags: cloneDeep(DefaultCustomTags),
		lastUpdateStore: null,
	},
	name: 'tags',
})
@Injectable()
export class TagsState implements NgxsOnInit {
	private matterId: string;
	private enabledDocumentFlows: MatterDocumentFlows = MatterDocumentFlows.HTML;

	private tagSelectors = [
		{
			selector: AdjustmentTagsSelector.adjustmentTags,
			name: 'adjustmentTags',
		},
		{
			selector: AppointmentTagSelector.appointmentTags,
			name: 'appointmentTags',
		},
		{ selector: BaseTagSelector.onMatterUpdate, name: 'onMatterUpdate' },
		{
			selector: BorrowerTagsSelector.GetBorrowerTags,
			name: 'GetBorrowerTags',
		},
		{ selector: BusinessTagSelector.businessTags, name: 'businessTags' },
		{
			selector: BusinessUserTagSelector.businessUsersTag,
			name: 'businessUsersTag',
		},
		{ selector: ClientTagsSelectors.clientTags, name: 'clientTags' },
		{
			selector: CommissionCalculatedTags.commissionTags,
			name: 'commissionTags',
		},
		{
			selector: ComplianceRequestsTagsSelector.getComplianceRequestTags,
			name: 'getComplianceRequestTags',
		},
		{ selector: DebtsTagsSelector.GetDebtsTags, name: 'GetDebtsTags' },
		{
			selector: EncumbranceTagsSelector.encumbranceTags,
			name: 'encumbranceTags',
		},
		{ selector: LTTTagsSelector.GetLTTTags, name: 'GetLTTTags' },
		{
			selector: MortgagesTagSelector.GetMortgagesTagSelector,
			name: 'GetMortgagesTagSelector',
		},
		{ selector: NotesTagsSelectors.getTagNotes, name: 'getTagNotes' },
		{
			selector: OtherPartyTagsSelector.GetOtherPartyTags,
			name: 'GetOtherPartyTags',
		},
		{ selector: PluralTagsSelector.PluralTags, name: 'PluralTags' },
		{
			selector: PropertyTagsSelector.GetPropertyTags,
			name: 'GetPropertyTags',
		},
		{
			selector: RequisitionTagsSelector.RequisitionTags,
			name: 'RequisitionTags',
		},
		{
			selector: SalutationsTagSelector.GetSalutationsTags,
			name: 'GetSalutationsTags',
		},
		{ selector: SignatureTagsSelectors.SignatureTags, name: 'SignatureTags' },
		{
			selector: SignatoriesTagSelectors.SignatoryTags,
			name: 'SignatoryTags',
		},
		{
			selector: StatementOfAccountTagsSelector.StatementOfAccountTags,
			name: 'StatementOfAccountTags',
		},
		{
			selector: TitleInsuranceTagsSelectors.TitleInsuranceTags,
			name: 'TitleInsuranceTags',
		},
		{
			selector: TrustLedgerTagsSelector.AmountInTrustTags,
			name: 'AmountInTrustTags',
		},
		{
			selector: TrustLedgerTagsSelector.TrustLedgerTags,
			name: 'TrustLedgerTags',
		},
		{
			selector: UndertakingsTagSelector.GetUndertakingsTags,
			name: 'GetUndertakingsTags',
		},
		{
			selector: PurchasersAndVendorTagsSelector.purchaserAndVendorTags,
			name: 'purchaserAndVendorTags',
		},
		{
			selector: JurisdictionsTagsSelector.getJurisdictionsTags,
			name: 'getJurisdictionsTags',
		},
		{
			selector: ClientIntakeSelector.clientIntakeTags,
			name: 'clientIntakeTags',
		},
	] as {
		selector: (state: unknown, ...states: unknown[]) => unknown;
		name: string;
	}[];

	constructor(
		private matterTagsRepo: TagRepoService,
		private db: AngularFirestore,
		private store: Store,
	) {}

	@Select(TagsState.tagsSelector) tags$: Observable<CustomTags>;
	@Select(SelectedMatter.id) matterId$: Observable<string>;
	@Select(SelectedMatter.getEnabledDocumentFlow)
	enabledDocumentFlows$: Observable<MatterDocumentFlows>;

	@Selector([TagsState])
	static getLastUpdateStore(state: TagModel): number {
		return state.lastUpdateStore;
	}

	@Selector([
		AdjustmentTagsSelector.adjustmentTags,
		AppointmentTagSelector.appointmentTags,
		BaseTagSelector.onMatterUpdate,
		BorrowerTagsSelector.GetBorrowerTags,
		BusinessTagSelector.businessTags,
		BusinessUserTagSelector.businessUsersTag,
		ClientTagsSelectors.clientTags,
		CommissionCalculatedTags.commissionTags,
		ComplianceRequestsTagsSelector.getComplianceRequestTags,
		DebtsTagsSelector.GetDebtsTags,
		EncumbranceTagsSelector.encumbranceTags,
		LTTTagsSelector.GetLTTTags,
		MortgagesTagSelector.GetMortgagesTagSelector,
		NotesTagsSelectors.getTagNotes,
		OtherPartyTagsSelector.GetOtherPartyTags,
		PluralTagsSelector.PluralTags,
		PropertyTagsSelector.GetPropertyTags,
		RequisitionTagsSelector.RequisitionTags,
		SalutationsTagSelector.GetSalutationsTags,
		SignatureTagsSelectors.SignatureTags,
		SignatoriesTagSelectors.SignatoryTags,
		StatementOfAccountTagsSelector.StatementOfAccountTags,
		TitleInsuranceTagsSelectors.TitleInsuranceTags,
		TrustLedgerTagsSelector.AmountInTrustTags,
		TrustLedgerTagsSelector.TrustLedgerTags,
		UndertakingsTagSelector.GetUndertakingsTags,
		PurchasersAndVendorTagsSelector.purchaserAndVendorTags,
		JurisdictionsTagsSelector.getJurisdictionsTags,
		ClientIntakeSelector.clientIntakeTags,
	])
	static tagsSelector(
		adjustmentTags: IAdjustmentTags,
		appointmentTags: AppointmentTags,
		baseTag: BaseTag,
		borrowerTags: BorrowerTags,
		businessTags: BusinessTags,
		usersOnFileTags: UsersOnFileTags,
		clientTags: ClientTags,
		commissionTags: CommissionTags,
		complianceRequestTags: ComplianceRequestTags,
		debtsTags: DebtsTags,
		encumbranceTags: EncumbranceTags,
		_LTTDepositTags: LTTDepositTags,
		mortgagesTags: MortgagesTags,
		noteTags: NoteTags,
		otherPartyTags: OtherPartyTags,
		plurals: ClientPlurals,
		propertyTags: PropertyTags,
		requisitionTags: RequisitionTags,
		salutationTags: SalutationTags,
		sigatureTags: SignatureTags,
		signatoryTags: SignatoryTags,
		statementOfAccountTags: StatementOfAccountTags,
		titleInsuranceTag: TitleInsuranceTag,
		amountInTrustTags: AmountInTrustTags,
		trustLedgerTags: TrustLedgerTags,
		undertakingTags: UndertakingTags,
		purchaserAndVendorTags: PurchaserAndVendorTags,
		jurisdictionsTags: JurisdictionsTags,
		clientIntakeTags: ClientIntakeFormTags,
	): CustomTags {
		const tags = {
			...adjustmentTags,
			...appointmentTags,
			...baseTag,
			...borrowerTags,
			...businessTags,
			...usersOnFileTags,
			...clientTags,
			...commissionTags,
			...complianceRequestTags,
			...debtsTags,
			...encumbranceTags,
			..._LTTDepositTags,
			...mortgagesTags,
			...noteTags,
			...otherPartyTags,
			...plurals,
			...propertyTags,
			...requisitionTags,
			...salutationTags,
			...sigatureTags,
			...signatoryTags,
			...statementOfAccountTags,
			...titleInsuranceTag,
			...amountInTrustTags,
			...trustLedgerTags,
			...undertakingTags,
			...purchaserAndVendorTags,
			...jurisdictionsTags,
			...clientIntakeTags,
		};

		return tags;
	}

	ngxsOnInit(ctx?: StateContext<TagModel>): void {
		this.enabledDocumentFlows$.subscribe((value) => {
			this.enabledDocumentFlows = value;
		});

		this.tagSelectors.forEach((selectorDetails) => {
			const tagsSelectorName = selectorDetails.name;

			combineLatest([
				this.store.select(selectorDetails.selector),
				this.matterId$,
			])
				.pipe(
					debounceTime(5000),
					switchMap(async ([tags, matterId]: [Partial<CustomTags>, string]) => {
						if (matterId) {
							await this.addTagsToFirestore(tags, tagsSelectorName);
						}

						return tags;
					}),
					retry(3),
				)
				.subscribe(
					(newTags) => {
						const oldTags = ctx.getState().tags;
						ctx.patchState({
							tags: { ...oldTags, ...newTags },
							lastUpdateStore: Date.now(),
						});
					},
					(err) => Logger.exception(err),
				);
		});

		this.matterId$
			.pipe(
				switchMap((matterId) => {
					if (matterId !== this.matterId) {
						this.matterId = matterId;
						ctx.patchState({ tags: cloneDeep(DefaultCustomTags) });
					}

					return of(null);
				}),
			)
			.subscribe();
	}

	@Action(ForceAddTagsToFirestore)
	forceAddTagsToFirestore() {
		this.tagSelectors.forEach((selectorDetails) => {
			const tagsSelectorName = selectorDetails.name;

			combineLatest([
				this.store.select(selectorDetails.selector),
				this.matterId$,
			])
				.pipe(
					take(1),
					switchMap(async ([tags, matterId]: [Partial<CustomTags>, string]) => {
						if (matterId) {
							await this.addTagsToFirestore(tags, tagsSelectorName);
						}

						return tags;
					}),
					retry(3),
				)
				.subscribe({ error: (err) => Logger.error(err) });
		});
	}

	private isDateTag = (value: unknown): value is TagsDate => {
		return (
			typeof value === 'object' &&
			value !== null &&
			'long' in value &&
			typeof value['long'] === 'object' &&
			'lastDay' in value &&
			typeof value['lastDay'] === 'string'
		);
	};

	private processTags(
		tags: Partial<CustomTags>,
		filteredTags: Partial<CustomTags>,
		parentKey = '',
	) {
		try {
			for (const key in tags) {
				if (Object.prototype.hasOwnProperty.call(tags, key)) {
					const value = tags[key];
					const fullKey = parentKey ? `${parentKey}${key}` : key;

					if (typeof value === 'object' && this.isDateTag(value)) {
						filteredTags[key] = value?.toString() || '';
					} else if (typeof value === 'object') {
						this.processTags(value, filteredTags[key], fullKey);
					}
				}
			}
		} catch (err) {
			console.log('Error processing tags', err);
		}
	}

	private async addTagsToFirestore(
		tags: Partial<CustomTags>,
		tagsDocumentName: string,
	) {
		if (
			this.matterId &&
			(this.enabledDocumentFlows === MatterDocumentFlows.WORD ||
				this.enabledDocumentFlows === MatterDocumentFlows.HTML_WORD ||
				this.enabledDocumentFlows === MatterDocumentFlows.LEAP)
		) {
			try {
				const filteredTags = cloneDeep(tags);
				this.processTags(tags, filteredTags);

				const tagPath = this.matterTagsRepo.DocPath(tagsDocumentName, {
					matterId: this.matterId,
				});

				await this.db.doc(tagPath).set(filteredTags);

				console.log('Tags saved to Firestore');
			} catch (error) {
				Logger.error(
					`Error setting tags in Firestore: ${JSON.stringify(error)}`,
				);
				throw new Error(error);
			}
		}
	}
}
